import React from "react";
import { Bar } from "react-chartjs-2";
import {
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
} from "chart.js/auto";

Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Simple plugin to draw per-month percent labels when dataset.monthly is present
const dataLabelPlugin = {
  id: "dataLabelPlugin",
  afterDatasetsDraw: (chart: any) => {
    const { ctx, data } = chart as any;
    ctx.save();
    data.datasets.forEach((dataset: any, dsIndex: number) => {
      const meta = chart.getDatasetMeta(dsIndex);
      if (!Array.isArray(dataset.monthly)) return; // only datasets with monthly comparisons
      meta.data.forEach((element: any, index: number) => {
        const change = dataset.monthly[index];
        const currVal = dataset.data && dataset.data[index];
        // show label only when current dataset has data and a valid percent comparison
        if (change === null || change === undefined) return;
        if (currVal === null || currVal === undefined) return;
        const x =
          element.x ?? (element?.getCenterPoint && element.getCenterPoint().x);
        const y =
          (element.y ??
            (element?.getCenterPoint && element.getCenterPoint().y)) - 8;
        ctx.fillStyle =
          dataset.backgroundColor ||
          (change > 0 ? "green" : change < 0 ? "red" : "#666");
        ctx.font = "11px Arial";
        ctx.textAlign = "center";
        const arrow = change > 0 ? "▲" : change < 0 ? "▼" : "";
        ctx.fillText(`${arrow} ${Math.abs(change)}%`, x, y);
      });
    });
    ctx.restore();
  },
};

Chart.register(dataLabelPlugin as any);

const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "bottom" as const,
    },
    tooltip: {
      callbacks: {
        afterLabel: (context: any) => {
          const dataset = context.dataset;
          if (!Array.isArray(dataset.monthly)) return "";
          const monthlyPercent = dataset.monthly[context.dataIndex];
          if (monthlyPercent === null || monthlyPercent === undefined)
            return "";
          const arrow =
            monthlyPercent > 0 ? "▲" : monthlyPercent < 0 ? "▼" : "—";
          return `Growth/Loss: ${arrow} ${Math.abs(monthlyPercent)}%`;
        },
      },
    },
  },
  elements: {
    bar: {
      // decrease bar width relative to category so there's more spacing between groups
      barPercentage: 0.6,
      categoryPercentage: 0.6,
      borderRadius: 6,
      maxBarThickness: 48,
    },
  },
  scales: {
    x: {
      ticks: { maxRotation: 45, autoSkip: true, maxTicksLimit: 6 },
      grid: { display: false },
    },
    y: {
      beginAtZero: true,
    },
  },
  title: {
    display: true,
    text: "Bar Chart",
  },
};

const styles = {
  container: {
    position: "relative" as "relative",
    width: "100%",
    height: "500px",
    overflowX: "auto" as "auto",
    "@media (max-width: 640px)": {
      height: "100px",
    },
  },
};

const BarChart = ({ chartData }: any) => {
  return (
    <div style={styles.container}>
      <Bar options={options} data={chartData} />
    </div>
  );
};

export default BarChart;
